import ExpandableView from "../../../../components/expandedView/expandableView";
import { __assignStatusClass, __isInternal, __roundToTwoDecimals, copyText, } from "../../../../utils/utils";
import './jobRow.scss';
import { CrawlfishJob } from "../../../crawler.interface";
import ToggleButton from "../../../../components/toggleButton/toggleButton";
import React from "react";
import { _cancelJob, _kickJob, _updateJob } from "../../../services/crawler.services";
import StatusCircle from "../../../../components/statusCircle/statusCircle";
import Button from "../../../../components/button/button";
import Modal from "../../../../components/modal/modal";
import InlineEdit from "../../../../components/inlineEdit/inlineEdit";
import { CheckmarkCircleIcon, CopyPage, DeleteTrashcanIcon, DownloadArrow, FolderRightArrowIcon, InlineEditIcon, RefreshIcon, RemoveIcon } from "../../../../components/icons/icons";
import { Link } from "react-router-dom";
import { __formatDate24HourUS } from "../../../../utils/dateTimeUtils";
import { InlineTypeAhead } from "../../../../components/inlineTypeAhead/inlineTypeAhead";
import { outsideClick } from "../../../../utils/hooks";
import UserContext, { UserContextInterface } from "src/app/contexts/user-context";
import { JobStatus } from "src/app/crawler/crawler.enums";
import { AiFileType } from "../../../../components/icons/icons";
import { _generateExportURLforJob } from "src/app/crawler/services/report.services";

export interface JobRowProps {
    jobData: CrawlfishJob;
    crawlfishUsers: string[];
    recreateExistingJob: (jobData: CrawlfishJob) => void;
    deleteJob: (jobId: number) => void;
    archiveJob: (jobId: number) => void;
}

function JobRow({ jobData, crawlfishUsers, recreateExistingJob, deleteJob, archiveJob }: JobRowProps) {

    const { user } = React.useContext<UserContextInterface>(UserContext);
    const [job, setJob] = React.useState<CrawlfishJob>(() => jobData);
    const [isArchiveModalOpen, openArchiveModal] = React.useState<boolean>(false);
    const [isDeleteModalOpen, openDeleteModal] = React.useState<boolean>(false);
    const [isCancelModalOpen, openCancelModal] = React.useState<boolean>(false);
    const [isModalLoading, setModalLoading] = React.useState<boolean>(false);
    const [isEditOwnerVisible, setEditOwnerVisible] = React.useState<boolean>(false);
    const [isKickModalOpen, setKickModalOpen] = React.useState<boolean>(false);

    const [updatedJob, setUpdatedJob] = React.useState({
        isEditModeName: false,
        isEditModeOwner: false,
        name: job.name,
        owner: job.owner
    });

    const uris: string[] = job.requested_uris.slice(0, 10);
    const userCanChangeOwner = crawlfishUsers?.length > 0 ? true : false;

    const outerRef = React.useRef<HTMLDivElement>(null);
    outsideClick(outerRef, () => setEditOwnerVisible(false));

    function handleUpdateJob(e: React.ChangeEvent<HTMLInputElement>): void {
        const key = e.target.name;
        const value = e.target.value;
        setUpdatedJob({ ...updatedJob, [key]: value });
    }

    async function saveUpdatedJob(inputName: string): Promise<void> {
        const updatedJobObj = { ...job };
        if (inputName === 'name') {
            updatedJobObj.name = updatedJob.name;
        } else if (inputName === 'owner') {
            updatedJobObj.owner = updatedJob.owner;
        }
        const newJob = await _updateJob(updatedJobObj);
        setJob(() => newJob);
        setUpdatedJob(() => ({
            isEditModeName: false,
            isEditModeOwner: false,
            name: newJob.name,
            owner: newJob.owner
        }));
        setEditOwnerVisible(false);
    }

    function setJobOwnerValue(newOwner: string): void {
        setUpdatedJob({ ...updatedJob, owner: newOwner });
    }

    async function handleReviewToggle(): Promise<void> {
        const newJobObj = job;
        newJobObj.reviewed = !newJobObj.reviewed;
        const updatedJob = await _updateJob(newJobObj);
        setJob(updatedJob);
    }

    function toggleNameEdit(): void {
        setUpdatedJob((currentState) => ({
            ...currentState,
            isEditModeName: !currentState.isEditModeName
        }));
    }

    async function handleCancelJob(): Promise<void> {
        const canceledJobData = await _cancelJob(jobData.id);
        openCancelModal(false);
        setJob(canceledJobData);
    }

    async function handleRecreateJob(): Promise<void> {
        recreateExistingJob(jobData);
    }

    async function handleDeleteJob(): Promise<void> {
        setModalLoading(true);
        deleteJob(job.id);
        setModalLoading(false);
    }

    async function handleArchiveJob(): Promise<void> {
        setModalLoading(true);
        archiveJob(job.id);
        setModalLoading(false);
    }

    async function handleKickJob(): Promise<void> {
        try {
            const kickJobResp = await _kickJob(job.id);
            setKickModalOpen(false);
            if (kickJobResp.StatusCode === 200) {
                alert(kickJobResp);
            } else {
                alert('Job Kicked');
            }
        } catch (err) {
            console.error(`Failed to kick job with id ${job.id}. Error: `, err);
        }
    }

    function showKickButton(): boolean {
        if ((__isInternal(user.username)) && (job.status === JobStatus.Queueing || job.status === JobStatus.Active)) {
            return true;
        }
        return false;
    }

    const kickJobModal =
        <div data-testid="kick-job-modal">
            <p style={{ textAlign: 'center' }}> This will look through the database for any page marked as being in &quot;queue&quot; in this job.
                If nothing is in queue, the job will be marked as done.
                If there is anything left in queue, it will be re-added to the queue in sqs. <br />
                Are you sure you want kick this job from the queue?
            </p>
            <div className="modal-footer">
                <div className="modal-footer-inner">
                    <Button variant="button button--solid button--danger" onClick={handleKickJob}>Kick</Button>
                    <Button variant="button button--neutral" onClick={() => setKickModalOpen(false)}>Cancel</Button>
                </div>
            </div>
        </div>;

    const cancelModalContent =
        <div data-testid="cancel-modal">
            <p>Are you sure you want to cancel this job? This action is permanent and cannot be undone. You can click the <q>recreate</q> button on a canceled job to create a new job with the same input data.</p>
            <div className="modal-footer">
                <div className="modal-footer-inner">
                    <Button variant="button button--solid button--danger" isLoading={isModalLoading} onClick={handleCancelJob}>Cancel Job</Button>
                    <Button variant="button button--neutral" onClick={() => openCancelModal(false)}>Cancel</Button>
                </div>
            </div>
        </div>;

    const deleteModalContent =
        <div data-testid="delete-modal">
            <p>Are you sure you want to delete this job? This action is permanent and cannot be undone and can take a few minutes.</p>
            <div className="modal-footer">
                <div className="modal-footer-inner">
                    <Button cyTestId="confirm-delete-button" variant="button button--solid button--danger" isLoading={isModalLoading} onClick={handleDeleteJob}>Delete</Button>
                    <Button cyTestId="cancel-delete-button" variant="button button--neutral" onClick={() => openDeleteModal(false)}>Cancel</Button>
                </div>
            </div>
        </div>;

    const archiveModalContent =
        <div data-testid="archive-modal">
            <p>Are you sure you want to archive this job? You will no longer see this job in the list, and will have to search for it individually to view the details.</p>
            <div className="modal-footer">
                <div className="modal-footer-inner">
                    <Button variant="button button--solid button--action" isLoading={isModalLoading} onClick={handleArchiveJob}>Archive</Button>
                    <Button variant="button button--neutral" onClick={() => openDeleteModal(false)}>Cancel</Button>
                </div>
            </div>
        </div>;

    const keywordCount = job.keywords?.body ? job.keywords.body.length : 0;
    const createdDate = __formatDate24HourUS(job.created_at).split(',');
    const createdDateDisplay = <>
        <span>{createdDate[0]}</span>&nbsp;
        <span>{createdDate[1]}</span>
    </>;

    const viewUsers = () => {
        if (userCanChangeOwner) {
            setEditOwnerVisible(true);
        }
    };

    const copyJobCost = () => {
        const jobCost = job?.info?.cost?.ai || 0;
        const jobCostValue = __roundToTwoDecimals(jobCost);
        copyText(jobCostValue);
    }

    return (
        <div className="index-row" key={job.id} data-testid="index-row">
            <div className="index-row-item job-name flex-wrap" data-testid="job-name">
                <div className="flex-nowrap">
                    <Link className="detail-link" to={`${job.id}`}>
                        <strong data-testid="job-name-text">
                            {job?.name ? job?.name : "Unnamed"}
                        </strong>
                    </Link>
                    <a className="inline-edit edit-job-name" role="button" onClick={toggleNameEdit}>
                        <InlineEditIcon />
                    </a>
                    {job?.rules?.ai_typing ? <span style={{ marginLeft: 6 }} title="This job uses AI Typing"><AiFileType /></span> : null}
                </div>
                <InlineEdit inputValue={updatedJob.name} inputName="name" isVisible={updatedJob.isEditModeName} handleOnChange={handleUpdateJob} handleOnSave={saveUpdatedJob} handleOnClose={() => setUpdatedJob((currentState) => ({ ...currentState, isEditModeName: false }))}
                />
                {showKickButton() ?
                    <>
                        <button className='kick-button' data-testid='kick-button' onClick={() => setKickModalOpen(true)}>Kick</button>
                        {
                            isKickModalOpen &&
                            <Modal isOpen={isKickModalOpen} openModal={setKickModalOpen} title={`Kick Job - ${job.name}`}>
                                {kickJobModal}
                            </Modal>
                        }
                    </>
                    :
                    null
                }
                <span className="sub-cell italicized" data-testid="depth-and-keywords">Depth={job?.rules?.max_crawl_depth}, Keywords={keywordCount} </span>
                <span className="sub-cell italicized" onClick={() => copyText(job.id.toString())}>
                    <CopyPage />
                    Jobs ID={job.id}
                </span>
            </div>

            <div className="index-row-item job-owner" ref={outerRef} data-testid="job-owner">
                {!isEditOwnerVisible ?
                    <a className={"inline-edit edit-job-owner edit-with-inline " + (userCanChangeOwner ? 'editable' : 'un-editable')} role="button" onClick={viewUsers} title={job.owner}>
                        <span className="job-owner-single">
                            {job.owner || 'n/a'}
                        </span>
                        {userCanChangeOwner &&
                            <InlineEditIcon />
                        }
                    </a>
                    :
                    <div className="edit-owner-box flex-nowrap">
                        <InlineTypeAhead options={crawlfishUsers} setValue={setJobOwnerValue} alwaysShowOptions={true} />
                        <a className="save-edit" onClick={() => saveUpdatedJob('owner')}>
                            <CheckmarkCircleIcon />
                        </a>
                        <a className="remove-button" onClick={() => setEditOwnerVisible(false)} title="Cancel Edit" aria-disabled style={{ paddingBottom: "3px" }}>
                            <RemoveIcon width={10} />
                        </a>
                    </div>
                }
            </div>

            <div className="index-row-item domains" data-testid="domains">
                <span className="domain-count">
                    {job.requested_uris.length}
                </span>
                {uris.length > 1 ?
                    <ExpandableView items={uris} isExpanded={false} />
                    : null
                }
                <span className="domain-single">{uris[0]}</span>
            </div>

            <div className="index-row-item status" data-testid="status">
                <span>
                    <StatusCircle statusClass={__assignStatusClass(job.status)} />
                    {job?.status ? job.status : "N/A"}
                </span>
            </div>

            <div className="index-row-item created-date" data-testid="created-date">
                <span>
                    {createdDateDisplay}
                </span>
            </div>

            <div className="index-row-item reviewed" data-testid="reviewed">
                <ToggleButton handleClick={handleReviewToggle} label={
                    job.reviewed ?
                        <label className="italicized">Reviewed</label>
                        :
                        <label>Review</label>
                } isOn={!job.reviewed} />
            </div>

            <div className="index-row-item job-buttons">
                {(job.status == JobStatus.Queueing || job.status === 'active') &&
                    <a className="remove-button" onClick={() => openCancelModal(true)} title="Cancel Job" aria-disabled data-testid="remove-button">
                        <RemoveIcon />
                    </a>
                }
                {cancelModalContent &&
                    <Modal isOpen={isCancelModalOpen} openModal={openCancelModal} title={`Cancel Job - ${job.name}`} >
                        {cancelModalContent}
                    </Modal>
                }
                {job.status == 'complete' &&
                    <a className="export-button" onClick={() => _generateExportURLforJob(job.id, job.name)} data-testid="export-button" title="Export all complete pages in the Job. Limit is 1,000,000 pages." >
                        <DownloadArrow />
                    </a>
                }
                <a className="refresh-icon-btn" title="Recreate Job" onClick={handleRecreateJob} data-testid="refresh-icon-btn">
                    <RefreshIcon />
                </a>
                <a className="delete-icon-btn" title="Delete Job" onClick={() => openDeleteModal(true)} data-testid="delete-icon-btn">
                    <DeleteTrashcanIcon />
                </a>
                {isDeleteModalOpen && <Modal isOpen={isDeleteModalOpen} openModal={openDeleteModal} title={`Delete Job - ${job.name}`}>
                    {deleteModalContent}
                </Modal>}
                <a className="archive-icon-btn" title="Archive Job" onClick={() => openArchiveModal(true)} data-testid="archive-icon-btn">
                    <FolderRightArrowIcon />
                </a>
                {isArchiveModalOpen && <Modal isOpen={isArchiveModalOpen} openModal={openArchiveModal} title={`Archive Job - ${job.name}`}>
                    {archiveModalContent}
                </Modal>}
            </div>

        </div >
    );
}

export default React.memo(JobRow);